import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  exportClass,
  importClass,
  templateClass,
} from "../../../api/pedagogicElement";
import { useAcademicYear } from "../../../core/common/context/AcademicYearContext";
import withPermission from "../../../core/common/autorisations/withPermission";
import {
  Action,
  buildPermission,
  Resource,
} from "../../../core/common/autorisations/permissionsMap";

type Props = {
  updateData?: () => void;
};

// bouton protégée pour importer
const ProtectedExcelImport = withPermission(
  (props: any) => (
    <div className="pe-1 mb-2">
      <OverlayTrigger
        placement="top"
        overlay={<Tooltip id="tooltip-top">Importer</Tooltip>}
      >
        <button
          type="button"
          className="btn btn-outline-light bg-white btn-icon me-1"
          onClick={() => {
            const input = document.createElement("input");
            input.type = "file";
            input.accept = ".xlsx, .xls";
            input.onchange = (e) => {
              const files = (e.target as HTMLInputElement).files;
              if (files && files.length > 0) {
                props.handleImportExcel(files[0]);
              }
            };
            input.click();
          }}
        >
          <i className="ti ti-file-import" />
        </button>
      </OverlayTrigger>
    </div>
  ),
  [buildPermission(Resource.CLASS, Action.CREATE)],
  "masquer",
);

// bouton protégée pour exporter
const ProtectedExcelExport = withPermission(
  (props: any) => (
    <div className="dropdown me-2 mb-2">
      <Link
        to="#"
        className="dropdown-toggle btn btn-light fw-medium d-inline-flex align-items-center"
        data-bs-toggle="dropdown"
      >
        <i className="ti ti-file-export me-2" />
        Exporter
      </Link>
      <ul className="dropdown-menu  dropdown-menu-end p-3">
        <li>
          <button
            className="dropdown-item rounded-1"
            onClick={props.getExcelFileTemplate}
          >
            <i className="ti ti-upload me-1" />
            Template
          </button>
        </li>
        <li>
          <button
            onClick={props.handleExportExcel}
            className="dropdown-item rounded-1"
          >
            <i className="ti ti-file-type-xls me-1" />
            Exporter en Excel{" "}
          </button>
        </li>
      </ul>
    </div>
  ),
  [buildPermission(Resource.CLASS, Action.READ)],
  "masquer",
);

const TooltipOption = ({ updateData }: Props) => {
  const { selectedYear } = useAcademicYear();

  const handleExportExcel = () => {
    if (!selectedYear) return;
    toast.promise(exportClass(selectedYear?.id), {
      loading: "Exportation en cours...",
      success: (data) => {
        // console.log("data export: ",data)
        const blob = new Blob([data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = URL.createObjectURL(blob);

        // Créer un lien temporaire
        const a = document.createElement("a");
        a.href = url;

        a.download = `classExport_${selectedYear.label}.xlsx`;
        a.click();
        return "Exporté avec succès";
      },
      error: (error: any) => {
        // console.error("Error:export user Excel ", error);
        return (
          error.message ||
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Une erreur s'est produit, Réessayer plus tard"
        );
      },
    });
  };

  const handleImportExcel = async (file: File) => {
    try {
      const formData = new FormData();
      formData.append("file", file); // Assuming file is an array of files

      // console.log("handleImportExcel called with formData:", formData);
      if (!selectedYear) return;

      toast.promise(importClass(formData, selectedYear.id), {
        loading: "Importation en cours...",
        success: () => {
          if (updateData) updateData();
          return "Importé avec succès";
        },
        error: (error: any) => {
          // console.error("Error:import class Excel ", error);

          const messageError =
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message ||
            "Une erreur s'est produit, Réessayer plus tard";
          return (
            <div>
              <strong>{messageError}</strong>
              {error.response?.data?.errors &&
                error.response.data.errors.map((errorItem: any) => (
                  <div>
                    Ligne {errorItem?.row}: {errorItem?.message}
                  </div>
                ))}
            </div>
          );
        },
      });
    } catch (error: any) {
      // console.error("Error:import class Excel ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  const getExcelFileTemplate = async () => {
    try {
      const response = await templateClass();
      const blob = new Blob([response], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      // Créer un lien temporaire
      const a = document.createElement("a");
      a.href = url;

      a.download = `classBasicTemplate.xlsx`;
      a.click();
      // console.log("response download template studentProfile: ",response)
    } catch (error: any) {
      // console.error("Error:download Excel template", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };
  return (
    <>
      <ProtectedExcelImport handleImportExcel={handleImportExcel} />
      <ProtectedExcelExport
        handleExportExcel={handleExportExcel}
        getExcelFileTemplate={getExcelFileTemplate}
      />
    </>
  );
};

export default TooltipOption;
